/**
 * CompletionCard — "Your data is ready" hero card for first-workspace state
 *
 * Shown when: source connected + model active + 0 dashboards.
 * Dismissible via localStorage.
 */

import React, { useCallback } from 'react';
import {
  Text,
  Button,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import {
  Dismiss16Regular,
  CheckmarkCircle16Filled,
  Circle16Regular,
} from '@fluentui/react-icons';
import { useNavigate } from 'react-router-dom';
import { useDataModels } from '../../contexts/DataModelsContext';
import { dismissWelcomeCard } from '../../hooks/useHomeFirstRunState';

// ─── Styles ──────────────────────────────────────────────────────────────────

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
    ...shorthands.padding('20px', '24px'),
    ...shorthands.borderRadius('10px'),
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke3),
    backgroundColor: tokens.colorNeutralBackground2,
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
    position: 'relative',
  },
  dismissBtn: {
    position: 'absolute',
    top: '10px',
    right: '10px',
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
  },
  description: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground2,
    lineHeight: '18px',
  },
  steps: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    ...shorthands.padding('4px', '0'),
  },
  step: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '13px',
  },
  stepDone: {
    color: tokens.colorNeutralForeground2,
  },
  stepPending: {
    color: tokens.colorNeutralForeground3,
  },
  stepIconDone: {
    color: tokens.colorStatusSuccessForeground1,
    flexShrink: 0,
  },
  stepIconPending: {
    color: tokens.colorNeutralForeground4,
    flexShrink: 0,
  },
  helperText: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
    lineHeight: '16px',
  },
  actions: {
    display: 'flex',
    gap: '8px',
    ...shorthands.padding('2px', '0'),
  },
});

// ─── Component ───────────────────────────────────────────────────────────────

interface CompletionCardProps {
  onDismiss: () => void;
}

const CompletionCard: React.FC<CompletionCardProps> = ({ onDismiss }) => {
  const styles = useStyles();
  const navigate = useNavigate();
  const { dataModels } = useDataModels();

  const handleDismiss = useCallback(() => {
    dismissWelcomeCard();
    onDismiss();
  }, [onDismiss]);

  const handleContinue = useCallback(() => {
    const activeModel = dataModels.find(m => m.status === 'active');
    if (activeModel) {
      navigate(`/data-models/${activeModel.id}`);
    } else if (dataModels.length > 0) {
      navigate(`/data-models/${dataModels[0].id}`);
    } else {
      navigate('/data-models');
    }
  }, [dataModels, navigate]);

  return (
    <div className={styles.root}>
      <Button
        className={styles.dismissBtn}
        appearance="subtle"
        size="small"
        icon={<Dismiss16Regular />}
        onClick={handleDismiss}
        aria-label="Dismiss"
      />

      <Text className={styles.title}>Your data is ready</Text>
      <Text className={styles.description}>
        AI analyzed your data and created a business model. You're ready to generate your first dashboard.
      </Text>

      {/* Next-step indicators */}
      <div className={styles.steps}>
        <div className={`${styles.step} ${styles.stepDone}`}>
          <CheckmarkCircle16Filled className={styles.stepIconDone} />
          <span>Data source connected</span>
        </div>
        <div className={`${styles.step} ${styles.stepDone}`}>
          <CheckmarkCircle16Filled className={styles.stepIconDone} />
          <span>Business model created</span>
        </div>
        <div className={`${styles.step} ${styles.stepPending}`}>
          <Circle16Regular className={styles.stepIconPending} />
          <span>First dashboard not created yet</span>
        </div>
      </div>

      <div className={styles.actions}>
        <Button appearance="primary" size="small" onClick={handleContinue}>
          Continue Working
        </Button>
      </div>

      <Text className={styles.helperText}>
        Your first dashboard is created from your Business Model, where AI has already prepared recommended dashboard starters.
      </Text>
    </div>
  );
};

export default CompletionCard;